import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Play, Stop, Trash } from "@phosphor-icons/react";

function formatWhen(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function formatDur(sec) {
  if (sec == null || Number.isNaN(Number(sec))) return "";
  const s = Math.max(0, Math.floor(Number(sec)));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function formatCaller(n) {
  const s = String(n || "").trim();
  return s || "Unknown caller";
}

export default function TeamVoicemailInbox({ userRow, onClose, onChanged }) {
  const uid = userRow?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [playingId, setPlayingId] = useState("");
  const [busyId, setBusyId] = useState("");
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  const stopAudio = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      el.load();
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlayingId("");
  }, []);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/customer-team/users/${uid}/voicemail`);
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
    return () => stopAudio();
  }, [load, stopAudio]);

  const markHeard = (id) => {
    setItems((prev) => prev.map((m) => (
      m.id === id && !m.listened_at
        ? { ...m, listened_at: new Date().toISOString() }
        : m
    )));
    onChanged?.();
  };

  const play = async (row) => {
    if (!row?.id || !uid) return;
    if (playingId === row.id) {
      stopAudio();
      return;
    }
    setBusyId(row.id);
    setError("");
    try {
      const { data } = await api.get(
        `/customer-team/users/${uid}/voicemail/${row.id}/audio`,
        { responseType: "blob" },
      );
      markHeard(row.id);
      stopAudio();
      const url = URL.createObjectURL(data);
      urlRef.current = url;
      const el = audioRef.current;
      if (!el) return;
      el.src = url;
      await el.play();
      setPlayingId(row.id);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e) || "Could not play voicemail");
    } finally {
      setBusyId("");
    }
  };

  const remove = async (row) => {
    if (!row?.id || !uid) return;
    if (!window.confirm(`Delete voicemail from ${formatCaller(row.caller_number)}?`)) return;
    setBusyId(row.id);
    setError("");
    try {
      await api.delete(`/customer-team/users/${uid}/voicemail/${row.id}`);
      if (playingId === row.id) stopAudio();
      setItems((prev) => prev.filter((m) => m.id !== row.id));
      toast.success("Voicemail deleted");
      onChanged?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId("");
    }
  };

  const label = userRow?.name || userRow?.phone?.sip_username || "agent";

  return (
    <>
      <audio
        ref={audioRef}
        className="hidden"
        onEnded={stopAudio}
        data-testid="team-voicemail-audio"
      />
      <Dialog open={!!uid} onOpenChange={(v) => { if (!v) { stopAudio(); onClose?.(); } }}>
        <DialogContent className="max-w-lg" data-testid="team-voicemail-dialog">
          <DialogHeader>
            <DialogTitle>Voicemail — {label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[24rem] overflow-auto">
            {error && <p className="text-sm text-destructive" data-testid="team-voicemail-error">{error}</p>}
            {loading && !items.length ? (
              <p className="text-sm text-muted-foreground">Loading voicemails…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="team-voicemail-empty">
                No voicemails in this mailbox.
              </p>
            ) : items.map((m) => (
              <div
                key={m.id}
                className="flex items-start gap-2 rounded-md border border-border p-2.5"
                data-testid="team-voicemail-row"
                data-voicemail-id={m.id}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {formatCaller(m.caller_number)}
                    {!m.listened_at && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-700">New</span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {formatWhen(m.created_at)}
                    {m.duration_sec != null ? ` · ${formatDur(m.duration_sec)}` : ""}
                    {m.called_did ? ` · DID ${m.called_did}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-muted text-sky-700 disabled:opacity-40"
                  title={playingId === m.id ? "Stop" : "Play"}
                  aria-label={playingId === m.id ? "Stop" : "Play"}
                  disabled={busyId === m.id || !m.has_audio}
                  onClick={() => play(m)}
                  data-testid="team-voicemail-play"
                >
                  {playingId === m.id
                    ? <Stop className="w-4 h-4" weight="fill" />
                    : <Play className="w-4 h-4" weight="fill" />}
                </button>
                <button
                  type="button"
                  className="p-1.5 rounded hover:bg-destructive/10 text-destructive disabled:opacity-40"
                  title="Delete"
                  aria-label="Delete"
                  disabled={busyId === m.id}
                  onClick={() => remove(m)}
                  data-testid="team-voicemail-delete"
                >
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { stopAudio(); onClose?.(); }} data-testid="team-voicemail-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
